import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface ScoreMetric {
  name: string;
  value: number;
  maxValue: number;
  weight?: number;
  description?: string;
}

interface ScoreBreakdownProps {
  scoreType: 'AIVSS' | 'CVSS' | 'AARS';
  totalScore: number;
  maxScore?: number;
  riskLevel: RiskLevel;
  metrics: ScoreMetric[];
  description?: string;
}

const riskColors: Record<RiskLevel, string> = {
  critical: 'bg-risk-critical',
  high: 'bg-risk-high',
  medium: 'bg-risk-medium',
  low: 'bg-risk-low',
  info: 'bg-risk-info',
};

const scoreTitles = {
  AIVSS: 'AI Vulnerability Severity Score',
  CVSS: 'Common Vulnerability Scoring System',
  AARS: 'AI Agent Risk Score',
};

const scoreDescriptions = {
  AIVSS:
    'Measures AI-specific vulnerabilities including prompt injection susceptibility, model manipulation risks, and AI safety concerns.',
  CVSS: 'Industry-standard scoring system for assessing the severity of security vulnerabilities based on exploitability and impact.',
  AARS: 'Evaluates risks specific to autonomous AI agents including autonomy level, scope boundaries, and oversight mechanisms.',
};

const scoreMethodologies: Record<string, { formula: string; detail: string; caveat: string }> = {
  AIVSS: {
    formula: 'AIVSS = Exploitability (30%) + AI Risk (35%) + Impact (35%)',
    detail:
      'Exploitability evaluates attack surface (vector, complexity, privileges, user interaction). AI Risk measures autonomous agent factors (autonomy level, data access, tool permissions, blast radius). Impact uses the CVSS 3.1 impact formula across confidentiality, integrity, and availability. Additional adjustments are applied for AI-specific finding volume and creator trust.',
    caveat:
      'The breakdown below shows the actual sub-score values from this scan \u2014 these are the direct inputs to the AIVSS formula.',
  },
  CVSS: {
    formula:
      'CVSS uses the industry-standard CVSS 3.1 formula. Multiple findings are aggregated using diminishing-contribution scaling so the score reflects overall exposure, not a simple sum.',
    detail: '',
    caveat: 'The breakdown below shows the actual sub-score values from this scan.',
  },
  AARS: {
    formula:
      'AARS evaluates seven autonomy risk factors: Human Oversight (20%), Decision Authority (20%), Reversibility (15%), Impact Scope (15%), Time Pressure (15%), Transparency (8%), Error Handling (7%). The final score is on a 0\u2013100 scale.',
    detail: '',
    caveat: 'The breakdown below shows the actual factor values from this scan.',
  },
};

const ScoreBreakdown = ({
  scoreType,
  totalScore,
  maxScore = 10,
  riskLevel,
  metrics,
  description,
}: ScoreBreakdownProps) => {
  return (
    <div className="card-elevated p-6 animate-slide-up">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-foreground">{scoreType} Breakdown</h3>
          <p className="text-sm text-muted-foreground">{scoreTitles[scoreType]}</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'text-3xl font-bold',
              riskLevel === 'critical' && 'text-risk-critical',
              riskLevel === 'high' && 'text-risk-high',
              riskLevel === 'medium' && 'text-risk-medium',
              riskLevel === 'low' && 'text-risk-low',
              riskLevel === 'info' && 'text-risk-info'
            )}
          >
            {totalScore != null ? totalScore.toFixed(1) : "—"}
          </span>
          <span className="text-sm text-muted-foreground">/{maxScore}</span>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-6 border-l-2 border-primary/30 pl-4">
        {description || scoreDescriptions[scoreType]}
      </p>

      {scoreMethodologies[scoreType] && (
        <details className="mb-6 text-xs text-muted-foreground group">
          <summary className="cursor-pointer select-none font-medium hover:text-foreground transition-colors">
            How is this score calculated?
          </summary>
          <div className="mt-2 space-y-2 border-l-2 border-muted pl-4">
            <p className="font-semibold">{scoreMethodologies[scoreType].formula}</p>
            {scoreMethodologies[scoreType].detail && (
              <p>{scoreMethodologies[scoreType].detail}</p>
            )}
            <p className="italic">{scoreMethodologies[scoreType].caveat}</p>
          </div>
        </details>
      )}

      <h4 className="text-xs font-medium text-muted-foreground mb-3">Risk Dimension Breakdown</h4>

      <div className="space-y-4">
        {metrics.map((metric, index) => {
          const percentage = (metric.value / metric.maxValue) * 100;
          return (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">{metric.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">
                    {metric.value != null ? metric.value.toFixed(1) : "—"} / {metric.maxValue}
                  </span>
                  {metric.weight && (
                    <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                      {metric.weight}%
                    </span>
                  )}
                </div>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-700',
                    riskColors[riskLevel]
                  )}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              {metric.description && (
                <p className="text-xs text-muted-foreground">{metric.description}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreBreakdown;
